/** Storyboard / Shot semantic validity + current-result provenance.
 * VISTOERA_STORYBOARD_BOUND_CONTEXT_STALE_FAILURE_ARTIFACT_STATE_CONSISTENCY_AND_INVALID_SHOT_GUARD_P0_V1
 *
 * Rules:
 * - Schema-valid ≠ product-valid (placeholder / missing-context refusals are invalid).
 * - Current result must match PRIMARY scriptContext identity (fingerprint).
 * - Invalid / mismatched results stay in history, never as current Result / shot list / downstream.
 */

import type { ShotArtifact, StoryboardArtifact } from '../../../api'

/** Minimal Primary Context shape — avoids circular import with storyboardGenerationCanvas. */
export type ScriptContextLike = {
  fingerprint?: string | null
  script?: { title?: unknown; synopsis?: unknown } | null
} | null | undefined

const REFUSAL_TITLE_PREFIXES = [
  '无法生成',
  '无法完成',
  'cannot generate',
  'unable to generate',
  'input missing',
  'unavailable_input',
]

const REFUSAL_TITLE_MARKERS = [
  '缺少必要输入',
  '缺少 script context',
  '缺少script context',
  'missing required input',
  'required input missing',
  'missing script context',
  'script context missing',
]

const FAKE_STYLE_TOKENS = new Set([
  'n/a',
  'na',
  'unknown',
  'default',
  'standard',
  'style',
  'none',
  'null',
  'placeholder',
  'tbd',
  '待定',
  '无',
  '未知',
])

const MAX_GENERATION_HISTORY = 20

export function hasValidStructuredScriptContext(scriptContext: ScriptContextLike): boolean {
  if (!scriptContext || !scriptContext.script) return false
  const title = String(scriptContext.script.title || '').trim()
  const synopsis = String(scriptContext.script.synopsis || '').trim()
  return title.length > 0 && synopsis.length > 0
}

function looksLikeRefusalTitle(titleRaw: string): boolean {
  const title = titleRaw.trim().toLowerCase()
  if (!title) return false
  if (REFUSAL_TITLE_PREFIXES.some((p) => title.startsWith(p.toLowerCase()))) return true
  if (REFUSAL_TITLE_MARKERS.some((m) => title.includes(m.toLowerCase()))) return true
  return false
}

/** Storyboard-level director / visual tone — required for downstream eligibility. */
export function isValidStoryboardStyle(styleRaw: unknown): boolean {
  const style = String(styleRaw ?? '').trim()
  if (!style) return false
  if (FAKE_STYLE_TOKENS.has(style.toLowerCase())) return false
  return style.length >= 2
}

/** Structural placeholder / schema-filler shot — not a production ShotArtifact. */
export function isValidShotArtifact(shot: unknown): shot is ShotArtifact {
  if (!shot || typeof shot !== 'object') return false
  const s = shot as Record<string, unknown>
  const title = String(s.title ?? '').trim()
  const description = String(s.description ?? '').trim()
  const camera = String(s.camera_description ?? '').trim()
  const blob = `${title}\n${description}\n${camera}`.toLowerCase()

  if (title === 'N/A' || title === 'n/a' || title === '-' || title === '—') return false
  if (description === 'N/A' || description === 'n/a') return false
  if (!description && !title) return false

  // Schema-filler / missing-context filler (language-agnostic structure signals)
  const mentionsSchema = blob.includes('schema') || blob.includes('结构化')
  const mentionsValid = blob.includes('valid') || blob.includes('有效')
  if (mentionsSchema && mentionsValid) return false
  if (blob.includes('script_context') || blob.includes('script context')) {
    if (
      blob.includes('missing') ||
      blob.includes('未提供') ||
      blob.includes('缺少') ||
      blob.includes('without') ||
      blob.includes('no ')
    ) {
      return false
    }
  }
  if (blob.includes('placeholder') && (mentionsSchema || blob.includes('keep'))) return false

  const duration = Number(s.duration_sec)
  const characters = Array.isArray(s.characters) ? s.characters : []
  const weakTitle = !title || title === 'N/A'
  if (weakTitle && characters.length === 0 && Number.isFinite(duration) && duration > 0 && duration <= 1) {
    if (description.length < 24) return false
  }

  return description.length >= 4 || (title.length >= 2 && title !== 'N/A')
}

export function validShotsFromStoryboard(storyboard: unknown): ShotArtifact[] {
  if (!storyboard || typeof storyboard !== 'object') return []
  const shots = (storyboard as StoryboardArtifact).shots
  if (!Array.isArray(shots)) return []
  return shots.filter(isValidShotArtifact)
}

export function isValidStoryboardArtifact(storyboard: unknown): storyboard is StoryboardArtifact {
  if (!storyboard || typeof storyboard !== 'object') return false
  const sb = storyboard as StoryboardArtifact
  const title = String(sb.title || '')
  if (looksLikeRefusalTitle(title)) return false
  if (!isValidStoryboardStyle(sb.style)) return false
  const synopsis = String(sb.synopsis || '').toLowerCase()
  if (
    synopsis.includes('script_context') &&
    (synopsis.includes('未提供') || synopsis.includes('缺少') || synopsis.includes('missing'))
  ) {
    // Refusal body without usable shots
    if (validShotsFromStoryboard(sb).length === 0) return false
  }
  return validShotsFromStoryboard(sb).length > 0
}

/** Primary Script Context identity used for provenance (not supplemental assets). */
export function primaryScriptContextIdentity(fields: Record<string, unknown>): string | null {
  const scriptContext = fields.scriptContext as ScriptContextLike
  if (!scriptContext) return null
  return scriptContext.fingerprint || null
}

export function resultPrimaryContextIdentity(fields: Record<string, unknown>): string | null {
  const atGen = (fields.paramsFingerprintAtGeneration as string | null) ?? null
  if (!atGen) return null
  try {
    const parsed = JSON.parse(atGen) as { script?: string | null }
    return parsed.script ?? null
  } catch {
    return null
  }
}

export function currentResultMatchesPrimaryContext(fields: Record<string, unknown>): boolean {
  if (!fields.storyboardResult) return true
  const current = primaryScriptContextIdentity(fields)
  if (!current) return false
  const fromResult = resultPrimaryContextIdentity(fields)
  if (fromResult == null) {
    // Legacy / refusal artifact without provenance cannot claim current match.
    return false
  }
  return fromResult === current
}

export function isCurrentStoryboardResultValid(fields: Record<string, unknown>): boolean {
  if (!hasValidStructuredScriptContext(fields.scriptContext as ScriptContextLike)) {
    return false
  }
  if (!isValidStoryboardArtifact(fields.storyboardResult)) return false
  return currentResultMatchesPrimaryContext(fields)
}

export interface StoryboardCurrentPresentation {
  /** Result shown in the Result area / shot list (null = empty ready or missing-context). */
  displayResult: StoryboardArtifact | null
  validShots: ShotArtifact[]
  validShotCount: number
  currentResultValid: boolean
  /** Raw stored result exists but must not be treated as current. */
  staleOrInvalidHeld: boolean
  /** Downstream Keyframe eligibility */
  keyframeDownstreamEligible: boolean
}

export function resolveCurrentStoryboardPresentation(
  fields: Record<string, unknown>,
): StoryboardCurrentPresentation {
  const raw = fields.storyboardResult as StoryboardArtifact | null
  const currentResultValid = isCurrentStoryboardResultValid(fields)
  if (currentResultValid && raw) {
    const validShots = validShotsFromStoryboard(raw)
    return {
      displayResult: raw,
      validShots,
      validShotCount: validShots.length,
      currentResultValid: true,
      staleOrInvalidHeld: false,
      keyframeDownstreamEligible: validShots.length > 0,
    }
  }
  return {
    displayResult: null,
    validShots: [],
    validShotCount: 0,
    currentResultValid: false,
    staleOrInvalidHeld: !!raw,
    keyframeDownstreamEligible: false,
  }
}

export function hasValidStoryboardForDownstream(fields: Record<string, unknown>): boolean {
  return resolveCurrentStoryboardPresentation(fields).keyframeDownstreamEligible
}

/**
 * Persist demotion: move non-current result into history, clear current.
 * Idempotent. Does not delete history entries.
 */
export function demoteNonCurrentStoryboardResult(fields: Record<string, unknown>): Record<string, unknown> {
  if (!fields.storyboardResult) return fields
  if (isCurrentStoryboardResultValid(fields)) return fields

  const history = Array.isArray(fields.generationHistory) ? (fields.generationHistory as unknown[]) : []
  const nextHistory = [
    {
      storyboard: fields.storyboardResult,
      version: fields.storyboardResultVersion ?? 1,
      completedAt: new Date().toISOString(),
      demotedReason: isValidStoryboardArtifact(fields.storyboardResult)
        ? 'PRIMARY_CONTEXT_MISMATCH'
        : 'INVALID_OR_PLACEHOLDER_ARTIFACT',
    },
    ...history,
  ].slice(0, MAX_GENERATION_HISTORY)

  const next: Record<string, unknown> = {
    ...fields,
    storyboardResult: null,
    storyboardArtifactId: null,
    // Keep version counter stable for history labeling; empty current.
    paramsFingerprintAtGeneration: null,
    generationHistory: nextHistory,
  }

  // Clear stale missing-context ERROR copy when binding is now present.
  if (hasValidStructuredScriptContext(fields.scriptContext as ScriptContextLike)) {
    const err = String(fields.runErrorMessage || '')
    if (
      /script context|剧本上下文|Primary Script Context|attach a Script/i.test(err) ||
      fields.runStatus === 'ERROR'
    ) {
      // Only clear ERROR if it looks like precondition failure, not unrelated failures.
      if (/script context|剧本|attach a Script|缺少/i.test(err) || !err) {
        next.runStatus = null
        next.runErrorMessage = null
      }
    }
  }

  return next
}

/** After Primary Context bind/rebind/unbind — demote if provenance no longer matches. */
export function reconcileStoryboardResultAfterContextChange(
  fields: Record<string, unknown>,
): Record<string, unknown> {
  return demoteNonCurrentStoryboardResult(fields)
}

export function assertStoryboardArtifactAcceptableForApply(
  storyboard: unknown,
): { ok: true; storyboard: StoryboardArtifact } | { ok: false; code: string; message: string } {
  if (!isValidStoryboardArtifact(storyboard)) {
    return {
      ok: false,
      code: 'INVALID_STORYBOARD_ARTIFACT',
      message: '模型返回的分镜结果无效（占位/拒绝输出），未写入当前结果',
    }
  }
  return { ok: true, storyboard: storyboard as StoryboardArtifact }
}

/** Canonical FE readiness — same predicate as Generate button + submit. */
export function storyboardGenerateReady(fields: Record<string, unknown>): boolean {
  if (!hasValidStructuredScriptContext(fields.scriptContext as ScriptContextLike)) return false
  // Production Format Contract — aspect_ratio + resolution must be explicit (never AUTO).
  const fmt = fields.productionFormat
  if (!fmt || typeof fmt !== 'object') return false
  const obj = fmt as Record<string, unknown>
  const aspect = String(obj.aspect_ratio ?? obj.aspectRatio ?? '').trim()
  const resolution = String(obj.resolution ?? '').trim().toLowerCase()
  if (!aspect || aspect.toUpperCase() === 'AUTO') return false
  if (!resolution || resolution === 'auto') return false
  return true
}
